import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Message } from "@/store/zustand/supabaseStore";

interface SubmittedAnswers {
  answers: Array<{ questionId: string; answer: string | string[] }>;
  skipped: boolean;
}

interface ToolResultsContextValue {
  getResult: (toolCallId: string) => any | undefined;
  getAllResults: () => Record<string, any>;
  setSubmittedAnswers: (toolCallId: string, data: SubmittedAnswers) => void;
  getSubmittedAnswers: (toolCallId: string) => SubmittedAnswers | undefined;
}

const ToolResultsContext = createContext<ToolResultsContextValue | null>(null);

export const useToolResults = (): ToolResultsContextValue => {
  const context = useContext(ToolResultsContext);
  if (!context) {
    return {
      getResult: () => undefined,
      getAllResults: () => ({}),
      setSubmittedAnswers: () => {},
      getSubmittedAnswers: () => undefined,
    };
  }
  return context;
};

interface ToolResultsProviderProps {
  children: React.ReactNode;
  messages: Message[];
}

export const ToolResultsProvider: React.FC<ToolResultsProviderProps> = ({
  children,
  messages,
}) => {
  // State for answers submitted by user but not yet confirmed by backend
  const [pendingAnswers, setPendingAnswers] = useState<
    Record<string, SubmittedAnswers>
  >({});

  const toolResultsMap = useMemo(() => {
    const map: Record<string, any> = {};

    for (const message of messages) {
      if (message.role !== "tool") continue;

      try {
        let contentArray: any[];
        if (typeof message.content === "string") {
          contentArray = JSON.parse(message.content);
        } else if (Array.isArray(message.content)) {
          contentArray = message.content;
        } else {
          continue;
        }

        for (const block of contentArray) {
          if (block.type === "tool-result" && block.toolCallId) {
            // Try multiple result locations (SDK versions differ)
            let result = block.content ?? block.result ?? block.output?.value;

            if (typeof result === "string") {
              try {
                result = JSON.parse(result);
              } catch {}
            }

            map[block.toolCallId] = result;
          }
        }
      } catch {
        // Ignore parsing errors
      }
    }

    return map;
  }, [messages]);

  const setSubmittedAnswers = useCallback(
    (toolCallId: string, data: SubmittedAnswers) => {
      setPendingAnswers((prev) => ({ ...prev, [toolCallId]: data }));
    },
    [],
  );

  const getSubmittedAnswers = useCallback(
    (toolCallId: string): SubmittedAnswers | undefined => {
      return pendingAnswers[toolCallId];
    },
    [pendingAnswers],
  );

  const value = useMemo(
    () => ({
      getResult: (toolCallId: string) => toolResultsMap[toolCallId],
      getAllResults: () => toolResultsMap,
      setSubmittedAnswers,
      getSubmittedAnswers,
    }),
    [toolResultsMap, setSubmittedAnswers, getSubmittedAnswers],
  );

  return (
    <ToolResultsContext.Provider value={value}>
      {children}
    </ToolResultsContext.Provider>
  );
};
